import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Zap, Layers, RefreshCw, Boxes, ScanLine } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useSubscription } from "@/context/SubscriptionContext";
import TrialBanner from "@/components/TrialBanner";

function UsageBar({ label, used, max }) {
  const unlimited = max == null;
  const pct = unlimited ? 0 : Math.min(100, (used / Math.max(1, max)) * 100);
  return (
    <div data-testid={`usage-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-slate-400">{label}</span>
        <span className="font-mono-num text-slate-200">
          {used.toLocaleString()}{unlimited ? "" : ` / ${max.toLocaleString()}`}
          {unlimited && <span className="text-emerald-400 ml-1">∞</span>}
        </span>
      </div>
      {!unlimited && (
        <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--pk-hover)" }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct > 90 ? "var(--pk-rose)" : "var(--pk-amber)" }} />
        </div>
      )}
    </div>
  );
}

export default function Billing() {
  const { sub, refresh } = useSubscription();
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState("");
  const [cycle, setCycle] = useState("monthly");

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setPricing(data)).catch(() => {});
    refresh();
  }, [refresh]);

  const checkout = async (lookupKey, id) => {
    setBusy(id);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy("");
    }
  };

  if (!sub || !pricing) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;
  }

  const planRank = { free: 0, hobby: 1, business: 2 };
  const currentRank = planRank[sub.plan] ?? 0;
  const slabActive = sub.addons?.slab;

  return (
    <div className="max-w-5xl mx-auto px-5 py-8 space-y-10" data-testid="billing-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-50">Billing &amp; Plans</h1>
        <p className="text-slate-400 mt-1 text-sm">Manage your subscription and add-ons.</p>
      </div>
      <TrialBanner />

      {/* Current plan + usage */}
      <div className="rounded-2xl p-6 border" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl flex items-center justify-center holo-shine border" style={{ borderColor: "var(--pk-border)" }}>
              <Zap className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Current plan</div>
              <div className="font-heading text-xl font-bold text-slate-100" data-testid="current-plan-name">{sub.plan_name}</div>
            </div>
          </div>
          <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-950/80 text-emerald-400 border border-emerald-800/60 capitalize">
            {sub.status}
          </span>
        </div>
        <div className="grid sm:grid-cols-3 gap-6 mt-6">
          <UsageBar label="Inventory" used={sub.usage.inventory} max={sub.limits.inventory_cap} />
          <UsageBar label="Team users" used={sub.usage.users} max={sub.limits.max_users} />
          {slabActive
            ? <UsageBar label="Slab lookups" used={sub.usage.slab_used} max={sub.usage.slab_quota} />
            : <div className="text-sm text-slate-500 flex items-end">No slab add-on active</div>}
        </div>
      </div>

      {/* Plans */}
      <div>
        <h2 className="font-heading text-lg font-bold text-slate-100 mb-3">Plans</h2>
        <div className="inline-flex items-center gap-1 p-1 rounded-xl border mb-5" style={{ borderColor: "var(--pk-border)", background: "var(--pk-card)" }} data-testid="billing-cycle-toggle">
          {[["monthly", "Monthly"], ["yearly", "Yearly · 2 months free"]].map(([v, l]) => (
            <button key={v} onClick={() => setCycle(v)} data-testid={`billing-cycle-${v}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${cycle === v ? "bg-amber-500 text-[#1a1205]" : "text-slate-400 hover:text-slate-100"}`}>{l}</button>
          ))}
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {pricing.plans.map((p) => {
            const isCurrent = p.id === sub.plan;
            const rank = planRank[p.id] ?? 0;
            return (
              <div key={p.id} data-testid={`billing-plan-${p.id}`}
                className={`rounded-2xl p-6 flex flex-col ${isCurrent ? "ring-2 ring-amber-500" : "border"}`}
                style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                <div className="font-heading font-bold text-slate-100">{p.name}</div>
                <div className="mt-2 font-heading text-3xl font-extrabold text-slate-50">
                  ${cycle === "yearly" && p.price_yearly ? p.price_yearly : p.price}
                  <span className="text-slate-500 text-sm font-normal">{p.price === 0 ? "/mo" : cycle === "yearly" && p.price_yearly ? "/yr" : "/mo"}</span>
                </div>
                {cycle === "yearly" && p.price_yearly && (
                  <div className="text-xs text-emerald-400 mt-1" data-testid={`billing-save-${p.id}`}>Save ${p.price * 12 - p.price_yearly}/yr</div>
                )}
                <ul className="mt-4 space-y-2 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <div className="mt-5 py-2.5 text-center text-sm font-semibold rounded-xl border text-slate-400" style={{ borderColor: "var(--pk-border)" }}>
                    Current plan
                  </div>
                ) : p.id === "free" ? (
                  <div className="mt-5 py-2.5 text-center text-sm text-slate-500">Downgrade via support</div>
                ) : (
                  <button
                    data-testid={`billing-upgrade-${p.id}`}
                    disabled={busy === p.id}
                    onClick={() => checkout(cycle === "yearly" && p.lookup_key_yearly ? p.lookup_key_yearly : p.lookup_key, p.id)}
                    className="mt-5 py-2.5 rounded-xl font-semibold text-sm bg-amber-500 text-[#1a1205] hover:bg-amber-400 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                    {busy === p.id && <Loader2 className="h-4 w-4 animate-spin" />}
                    {rank > currentRank ? "Upgrade" : "Switch"} to {p.name}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Add-ons */}
      <div>
        <h2 className="font-heading text-lg font-bold text-slate-100 mb-4">Add-ons</h2>

        <div className="rounded-2xl p-6 border mb-5" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
          <div className="flex items-center gap-2 mb-1">
            <ScanLine className="h-5 w-5 text-amber-400" />
            <div className="font-heading font-bold text-slate-100">Slab lookups (CardOS)</div>
          </div>
          <p className="text-sm text-slate-400 mb-4">Auto-fill PSA/BGS/CGC slab data, images and graded market price. Pick a monthly volume.</p>
          <div className="grid sm:grid-cols-3 gap-4">
            {pricing.slab_tiers.map((t) => {
              const active = slabActive === t.id;
              return (
                <div key={t.id} data-testid={`billing-slab-${t.id}`}
                  className={`rounded-xl p-4 ${active ? "ring-2 ring-amber-500" : "border"}`}
                  style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
                  <div className="font-semibold text-slate-100 text-sm">{t.name}</div>
                  <div className="font-heading text-xl font-extrabold text-slate-50 mt-1">${t.price}<span className="text-slate-500 text-xs font-normal">/mo</span></div>
                  <div className="text-xs text-slate-400 mt-0.5">{t.quota.toLocaleString()} lookups/mo</div>
                  {active ? (
                    <div className="mt-3 text-center text-xs font-semibold text-emerald-400">Active</div>
                  ) : (
                    <button data-testid={`billing-slab-cta-${t.id}`} disabled={busy === t.id}
                      onClick={() => checkout(t.lookup_key, t.id)}
                      className="mt-3 w-full py-2 rounded-lg text-xs font-semibold border text-slate-200 hover:bg-[var(--pk-hover)] transition-colors disabled:opacity-60"
                      style={{ borderColor: "var(--pk-border)" }}>
                      {busy === t.id ? "…" : "Subscribe"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <AddonCard icon={RefreshCw} name="Shopify Live Sync" price={25} active={sub.addons?.shopify}
            lookupKey="addon_shopify" id="shopify" busy={busy} onSub={checkout}
            desc="Real-time inventory sync and order webhooks with your Shopify store." />
          <AddonCard icon={Boxes} name="Boxed.gg Data" price={5} active={sub.addons?.boxed}
            lookupKey="addon_boxed" id="boxed" busy={busy} onSub={checkout}
            desc="See exactly which cards Boxed is buying and what they'll pay." />
        </div>
      </div>
    </div>
  );
}

function AddonCard({ icon: Icon, name, price, active, lookupKey, id, busy, onSub, desc }) {
  return (
    <div data-testid={`billing-addon-${id}`} className={`rounded-2xl p-6 ${active ? "ring-2 ring-amber-500" : "border"}`}
      style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-amber-400" />
          <div className="font-heading font-bold text-slate-100">{name}</div>
        </div>
        <div className="font-heading text-lg font-extrabold text-slate-50">${price}<span className="text-slate-500 text-xs font-normal">/mo</span></div>
      </div>
      <p className="text-sm text-slate-400 mt-2">{desc}</p>
      {active ? (
        <div className="mt-4 text-center py-2 rounded-xl text-sm font-semibold text-emerald-400 border border-emerald-800/60 bg-emerald-950/40">Active</div>
      ) : (
        <button data-testid={`billing-addon-cta-${id}`} disabled={busy === id} onClick={() => onSub(lookupKey, id)}
          className="mt-4 w-full py-2.5 rounded-xl font-semibold text-sm bg-amber-500 text-[#1a1205] hover:bg-amber-400 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          {busy === id && <Loader2 className="h-4 w-4 animate-spin" />} Subscribe
        </button>
      )}
    </div>
  );
}
